import React, {useMemo, useState} from 'react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import {useAuth} from '../authentication/AuthContext';
import {useStats} from './hooks/useStats';
import {useProjectStats} from './hooks/useProjectStats';
import {useProductivityScoreSeries} from './hooks/useProductivityScoreSeries';
import {
    CalendarDays,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    Clock3,
    Sparkles,
    TimerReset,
    Zap,
} from 'lucide-react';

function formatFocusTime(seconds = 0) {
    if (!seconds) return '0s';

    if (seconds < 60) return `${seconds}s`;

    if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return remainingSeconds ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (!minutes) return `${hours}h`;
    return `${hours}h ${minutes}m`;
}

const EMPTY_STATS = {
    completedTasks: 0,
    focusSeconds: 0,
    completedPomodoros: 0,
    reviewedNotes: 0,
    reviewedCards: 0,
    productivityScore: 0,
};

const PERIOD_OPTIONS = [
    {
        value: 'DAILY',
        title: 'Today',
        subtitle: 'A compact snapshot of tasks, focus time, and pomodoros for today.',
        badgeLabel: 'Today',
        projectTitle: 'today',
        projectBadgeLabel: 'Daily',
    },
    {
        value: 'WEEKLY',
        title: 'This week',
        subtitle: 'A compact summary of this week using the same productivity metrics.',
        badgeLabel: 'This week',
        projectTitle: 'this week',
        projectBadgeLabel: 'Weekly',
    },
    {
        value: 'MONTHLY',
        title: 'This month',
        subtitle: 'A compact monthly view to track consistency over a longer period.',
        badgeLabel: 'This month',
        projectTitle: 'this month',
        projectBadgeLabel: 'Monthly',
    },
    {
        value: 'ALL_TIME',
        title: 'All time',
        subtitle: 'A compact lifetime snapshot of completed work, focus time, and pomodoros.',
        badgeLabel: 'All time',
        projectTitle: 'all time',
        projectBadgeLabel: 'All time',
    },
];

const CHART_METRICS = [
    {value: 'productivityScore', label: 'Productivity score', unit: 'points'},
    {value: 'reviewedNotes', label: 'Reviewed notes', unit: 'notes'},
    {value: 'reviewedCards', label: 'Reviewed cards', unit: 'cards'},
    {value: 'completedPomodoros', label: 'Completed pomodoros', unit: 'pomodoros'},
    {value: 'completedTasks', label: 'Completed tasks', unit: 'tasks'},
    {value: 'focusSeconds', label: 'Focus time', unit: 'focus time'},
];

function formatDateForInput(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function formatSelectedDate(date) {
    return new Intl.DateTimeFormat(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(new Date(`${date}T00:00:00`));
}

function shiftDate(date, amount) {
    const shiftedDate = new Date(`${date}T12:00:00`);
    shiftedDate.setDate(shiftedDate.getDate() + amount);
    return formatDateForInput(shiftedDate);
}

function normalizeProjects(projectStatsResponse) {
    const projects = projectStatsResponse?.projects;

    if (!Array.isArray(projects)) {
        return [];
    }

    return [...projects]
        .sort((left, right) => (right.totalFocusSeconds ?? 0) - (left.totalFocusSeconds ?? 0))
        .slice(0, 5);
}

function StatCard({icon: Icon, label, value, hint, accentClass, badgeLabel = 'Today'}) {
    return (
        <div className="rounded-[22px] border border-white/10 bg-white/5 p-4 shadow-[0_12px_30px_rgba(0,0,0,0.18)] transition hover:-translate-y-0.5 hover:bg-white/10">
            <div className="mb-4 flex items-center justify-between">
                <div className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 ${accentClass}`}>
                    <Icon size={18}/>
                </div>
                <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">{badgeLabel}</span>
            </div>
            <div className="text-3xl font-bold tracking-tight text-gray-100">{value}</div>
            <div className="mt-1 text-sm font-medium text-gray-300">{label}</div>
            <div className="mt-2 text-xs leading-relaxed text-gray-500">{hint}</div>
        </div>
    );
}

function MetricRow({label, rawValue, displayValue, cap, tone, detail}) {
    const percent = Math.max(0, Math.min(100, cap ? Math.round((rawValue / cap) * 100) : 0));

    return (
        <div className="rounded-2xl border border-white/10 bg-[#202433] p-4">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <div className="text-sm font-medium text-gray-200">{label}</div>
                    <div className="mt-1 text-xs text-gray-500">{detail}</div>
                </div>
                <div className={`text-lg font-semibold ${tone}`}>{displayValue}</div>
            </div>
            <div className="mt-4 h-2 rounded-full bg-white/5">
                <div
                    className={`h-2 rounded-full bg-gradient-to-r ${tone}`}
                    style={{width: `${percent}%`}}
                />
            </div>
        </div>
    );
}

function ProjectStatsSection({title, badgeLabel, projects, hasError}) {
    return (
        <div className="mt-4 rounded-[24px] border border-white/10 bg-[#1A1E2B] p-5 shadow-[0_12px_30px_rgba(0,0,0,0.16)]">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h3 className="text-base font-semibold text-gray-100">Top projects - {title}</h3>
                    <p className="mt-1 text-xs text-gray-500">Focus and pomodoro totals grouped by project for {badgeLabel.toLowerCase()}.</p>
                </div>
                <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.16em] text-gray-400">
                    {badgeLabel}
                </div>
            </div>

            {hasError ? (
                <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                    Project stats could not be loaded.
                </div>
            ) : null}

            {!hasError && projects.length === 0 ? (
                <div className="mt-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-gray-400">
                    No project stats yet.
                </div>
            ) : null}

            {!hasError && projects.length > 0 ? (
                <div className="mt-4 overflow-hidden rounded-xl border border-white/10">
                    <div className="grid grid-cols-[1.3fr_0.7fr_0.7fr] bg-white/5 px-4 py-2 text-[11px] uppercase tracking-[0.16em] text-gray-500">
                        <span>Project</span>
                        <span className="text-right">Focus</span>
                        <span className="text-right">Pomodoros</span>
                    </div>
                    <div className="divide-y divide-white/10">
                        {projects.map((project) => (
                            <div
                                key={project.projectId}
                                className="grid grid-cols-[1.3fr_0.7fr_0.7fr] items-center px-4 py-3 text-sm"
                            >
                                <span className="truncate pr-4 text-gray-200">{project.projectName || 'Untitled project'}</span>
                                <span className="text-right font-medium text-cyan-300">{formatFocusTime(project.totalFocusSeconds ?? 0)}</span>
                                <span className="text-right font-medium text-violet-300">{project.completedPomodoros ?? 0}</span>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function ProductivityScoreChart({period, points, isLoading, hasError}) {
    const [selectedMetric, setSelectedMetric] = useState('productivityScore');
    const isWeekly = period === 'WEEKLY';
    const metric = CHART_METRICS.find((option) => option.value === selectedMetric) ?? CHART_METRICS[0];
    const title = `${isWeekly ? 'Weekly' : 'Monthly'} ${metric.label.toLowerCase()}`;
    const values = points.map((point) => Number(point[selectedMetric]) || 0);
    const maxValue = Math.max(1, ...values);
    const totalValue = values.reduce((total, value) => total + value, 0);
    const formatMetricValue = (value) => selectedMetric === 'focusSeconds' ? formatFocusTime(value) : value;

    return (
        <section className="mt-4 rounded-[24px] border border-white/10 bg-[#1E2230] p-5 shadow-[0_12px_30px_rgba(0,0,0,0.16)]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-gray-300">{title}</h2>
                    <p className="mt-1 text-xs text-gray-500">{metric.label} recorded each day.</p>
                </div>
                <div className="flex items-center gap-3 self-stretch sm:self-auto">
                    <label className="flex-1 sm:flex-none">
                        <span className="sr-only">Chart metric</span>
                        <select
                            value={selectedMetric}
                            onChange={(event) => setSelectedMetric(event.target.value)}
                            className="w-full rounded-xl border border-white/10 bg-[#171B27] px-3 py-2 text-xs font-medium text-gray-200 outline-none transition focus:border-cyan-400/50 sm:w-auto"
                        >
                            {CHART_METRICS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </label>
                    <div className="min-w-20 text-right">
                        <div className="text-2xl font-bold text-gray-100">{formatMetricValue(totalValue)}</div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-gray-500">Total {metric.unit}</div>
                    </div>
                </div>
            </div>

            {hasError ? (
                <div className="mt-5 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                    Score history could not be loaded.
                </div>
            ) : null}

            {isLoading ? (
                <div className="mt-5 h-48 animate-pulse rounded-xl bg-white/5"/>
            ) : null}

            {!isLoading && !hasError ? (
                <div className="mt-5 overflow-x-auto pb-1">
                    <div
                        className={`grid h-52 items-end gap-2 ${isWeekly ? 'min-w-[520px] grid-cols-7' : 'min-w-[900px]'}`}
                        style={isWeekly ? undefined : {gridTemplateColumns: `repeat(${points.length}, minmax(18px, 1fr))`}}
                    >
                        {points.map((point) => {
                            const value = Number(point[selectedMetric]) || 0;
                            const date = new Date(`${point.date}T00:00:00`);
                            const label = isWeekly
                                ? new Intl.DateTimeFormat(undefined, {weekday: 'short'}).format(date)
                                : date.getDate();
                            const height = value > 0 ? Math.max(8, (value / maxValue) * 160) : 4;

                            return (
                                <div key={point.date} className="group flex h-full min-w-0 flex-col justify-end" title={`${point.date}: ${formatMetricValue(value)} ${metric.unit}`}>
                                    <div className="mb-1 text-center text-[10px] font-medium text-gray-400 opacity-0 transition group-hover:opacity-100">{formatMetricValue(value)}</div>
                                    <div
                                        className="w-full rounded-t-md bg-gradient-to-t from-indigo-500 via-violet-500 to-cyan-400 transition group-hover:brightness-125"
                                        style={{height: `${height}px`}}
                                    />
                                    <div className="mt-2 truncate text-center text-[10px] text-gray-500">{label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </section>
    );
}

function LoadingShell() {
    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg)]">
            <Header/>
            <div className="flex flex-1 gap-3 bg-[var(--workspace-bg)] px-3 pb-4 pt-[76px] md:gap-4 md:px-4">
                <Sidebar/>
                <div className="min-w-0 flex-1 rounded-[10px] bg-primary p-4 md:p-5">
                    <div className="animate-pulse space-y-4">
                        <div className="h-48 rounded-[24px] border border-white/10 bg-white/5"/>
                        <div className="grid gap-4 lg:grid-cols-[1.4fr_0.9fr]">
                            <div className="h-72 rounded-[24px] border border-white/10 bg-white/5"/>
                            <div className="h-72 rounded-[24px] border border-white/10 bg-white/5"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export const Reports = () => {
    const {authToken, isLoading: authLoading} = useAuth();
    const [selectedPeriod, setSelectedPeriod] = useState('DAILY');
    const [selectedDate, setSelectedDate] = useState(null);
    const today = formatDateForInput();

    const selectedPeriodConfig = useMemo(() => {
        if (selectedPeriod === 'DAILY' && selectedDate) {
            const formattedDate = formatSelectedDate(selectedDate);
            return {
                ...PERIOD_OPTIONS[0],
                title: formattedDate,
                subtitle: `Tasks, focus time, and pomodoros recorded on ${formattedDate}.`,
                badgeLabel: formattedDate,
                projectTitle: formattedDate,
                projectBadgeLabel: 'Selected day',
            };
        }

        return PERIOD_OPTIONS.find((option) => option.value === selectedPeriod) ?? PERIOD_OPTIONS[0];
    }, [selectedDate, selectedPeriod]);

    const dailyQuery = useStats(authToken, 'DAILY', selectedDate);
    const weeklyQuery = useStats(authToken, 'WEEKLY');
    const monthlyQuery = useStats(authToken, 'MONTHLY');
    const allTimeQuery = useStats(authToken, 'ALL_TIME');
    const dailyProjectQuery = useProjectStats(authToken, 'DAILY', selectedDate);
    const weeklyProjectQuery = useProjectStats(authToken, 'WEEKLY');
    const monthlyProjectQuery = useProjectStats(authToken, 'MONTHLY');
    const allTimeProjectQuery = useProjectStats(authToken, 'ALL_TIME');
    const scoreSeriesQuery = useProductivityScoreSeries(authToken, selectedPeriod);

    if (authLoading) {
        return <LoadingShell/>;
    }

    if (
        dailyQuery.isLoading
        || weeklyQuery.isLoading
        || monthlyQuery.isLoading
        || allTimeQuery.isLoading
        || dailyProjectQuery.isLoading
        || weeklyProjectQuery.isLoading
        || monthlyProjectQuery.isLoading
        || allTimeProjectQuery.isLoading
    ) {
        return <LoadingShell/>;
    }

    const dailyStats = dailyQuery.data ?? EMPTY_STATS;
    const weeklyStats = weeklyQuery.data ?? EMPTY_STATS;
    const monthlyStats = monthlyQuery.data ?? EMPTY_STATS;
    const allTimeStats = allTimeQuery.data ?? EMPTY_STATS;

    const dailyProjects = normalizeProjects(dailyProjectQuery.data);
    const weeklyProjects = normalizeProjects(weeklyProjectQuery.data);
    const monthlyProjects = normalizeProjects(monthlyProjectQuery.data);
    const allTimeProjects = normalizeProjects(allTimeProjectQuery.data);

    const statsByPeriod = {
        DAILY: dailyStats,
        WEEKLY: weeklyStats,
        MONTHLY: monthlyStats,
        ALL_TIME: allTimeStats,
    };

    const projectsByPeriod = {
        DAILY: dailyProjects,
        WEEKLY: weeklyProjects,
        MONTHLY: monthlyProjects,
        ALL_TIME: allTimeProjects,
    };

    const projectErrorByPeriod = {
        DAILY: Boolean(dailyProjectQuery.error),
        WEEKLY: Boolean(weeklyProjectQuery.error),
        MONTHLY: Boolean(monthlyProjectQuery.error),
        ALL_TIME: Boolean(allTimeProjectQuery.error),
    };
    const hasSelectedData = Boolean(
        statsByPeriod[selectedPeriod].completedTasks
        || statsByPeriod[selectedPeriod].focusSeconds
        || statsByPeriod[selectedPeriod].completedPomodoros
        || statsByPeriod[selectedPeriod].reviewedNotes
        || statsByPeriod[selectedPeriod].reviewedCards
        || statsByPeriod[selectedPeriod].productivityScore
    );

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg)]">
            <Header/>
            <div className="flex flex-1 gap-3 bg-[var(--workspace-bg)] px-3 pb-4 pt-[76px] md:gap-4 md:px-4">
                <Sidebar/>

                <div className="min-w-0 flex-1 rounded-[10px] bg-primary p-4 md:p-5">
                    <div className="mb-4 flex flex-col gap-3 rounded-[18px] border border-white/10 bg-white/5 p-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-wrap gap-2">
                            {PERIOD_OPTIONS.map((option) => {
                                const isActive = option.value === selectedPeriod && !(option.value === 'DAILY' && selectedDate);

                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => {
                                            setSelectedPeriod(option.value);
                                            setSelectedDate(null);
                                        }}
                                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${isActive ? 'border-cyan-400/50 bg-cyan-400/15 text-cyan-200' : 'border-white/10 bg-transparent text-gray-400 hover:bg-white/10 hover:text-gray-200'}`}
                                    >
                                        {option.title}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedDate(shiftDate(selectedDate ?? today, -1));
                                    setSelectedPeriod('DAILY');
                                }}
                                aria-label="Previous day"
                                title="Previous day"
                                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-[#171B27] text-gray-400 transition hover:border-indigo-400/40 hover:bg-indigo-400/10 hover:text-indigo-200"
                            >
                                <ChevronLeft size={16}/>
                            </button>

                            <label className={`flex min-w-0 cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs transition ${selectedDate ? 'border-indigo-400/50 bg-indigo-400/15 text-indigo-100' : 'border-white/10 bg-[#171B27] text-gray-300 hover:border-white/20'}`}>
                                <CalendarDays size={15} className={`shrink-0 ${selectedDate ? 'text-indigo-300' : 'text-gray-500'}`}/>
                                <input
                                    type="date"
                                    value={selectedDate ?? ''}
                                    max={today}
                                    onChange={(event) => {
                                        const nextDate = event.target.value || null;
                                        setSelectedDate(nextDate);
                                        if (nextDate) setSelectedPeriod('DAILY');
                                    }}
                                    aria-label="Choose report date"
                                    className="min-w-0 cursor-pointer border-0 bg-transparent text-xs text-gray-100 [color-scheme:dark] focus:outline-none"
                                />
                            </label>

                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedDate(shiftDate(selectedDate, 1));
                                    setSelectedPeriod('DAILY');
                                }}
                                disabled={!selectedDate || selectedDate >= today}
                                aria-label="Next day"
                                title="Next day"
                                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-[#171B27] text-gray-400 transition hover:border-indigo-400/40 hover:bg-indigo-400/10 hover:text-indigo-200 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-white/10 disabled:hover:bg-[#171B27] disabled:hover:text-gray-400"
                            >
                                <ChevronRight size={16}/>
                            </button>
                        </div>
                    </div>

                    <section className="overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-[#20263A] via-[#1E2331] to-[#161A25] shadow-[0_18px_42px_rgba(0,0,0,0.26)]">
                        <div className="relative p-6 md:p-7">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(129,140,248,0.18),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(34,211,238,0.14),transparent_28%)]"/>
                            <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                                <div className="max-w-2xl">
                                    <div className="inline-flex items-center gap-2 rounded-full border border-indigo-300/20 bg-indigo-400/10 px-3 py-1 text-xs font-medium text-indigo-100">
                                        <Sparkles size={13}/>
                                        Productivity dashboard
                                    </div>
                                    <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-100 md:text-4xl">
                                        {selectedPeriodConfig.title}, in one clean snapshot.
                                    </h1>
                                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-gray-400 md:text-base">
                                        {selectedPeriodConfig.subtitle}
                                    </p>
                                </div>
                            </div>

                            <div className="relative mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                                <StatCard
                                    icon={CheckCircle2}
                                    label="Completed tasks"
                                    value={statsByPeriod[selectedPeriod].completedTasks}
                                    hint={`Tasks marked done ${selectedPeriodConfig.badgeLabel.toLowerCase()}.`}
                                    accentClass="bg-emerald-500/15 text-emerald-300"
                                    badgeLabel={selectedPeriodConfig.badgeLabel}
                                />
                                <StatCard
                                    icon={Clock3}
                                    label="Focus time"
                                    value={formatFocusTime(statsByPeriod[selectedPeriod].focusSeconds)}
                                    hint={`Total focused work time ${selectedPeriodConfig.badgeLabel.toLowerCase()}.`}
                                    accentClass="bg-cyan-500/15 text-cyan-300"
                                    badgeLabel={selectedPeriodConfig.badgeLabel}
                                />
                                <StatCard
                                    icon={TimerReset}
                                    label="Completed pomodoros"
                                    value={statsByPeriod[selectedPeriod].completedPomodoros}
                                    hint={`Pomodoros finished ${selectedPeriodConfig.badgeLabel.toLowerCase()}.`}
                                    accentClass="bg-violet-500/15 text-violet-300"
                                    badgeLabel={selectedPeriodConfig.badgeLabel}
                                />
                                <StatCard
                                    icon={Zap}
                                    label="Productivity score"
                                    value={statsByPeriod[selectedPeriod].productivityScore ?? 0}
                                    hint={`Cumulative score from completed pomodoros and tracked items ${selectedPeriodConfig.badgeLabel.toLowerCase()}.`}
                                    accentClass="bg-amber-500/15 text-amber-300"
                                    badgeLabel={selectedPeriodConfig.badgeLabel}
                                />
                            </div>
                        </div>
                    </section>

                    {dailyQuery.error || weeklyQuery.error || monthlyQuery.error || allTimeQuery.error ? (
                        <div className="mt-4 rounded-[24px] border border-red-500/20 bg-red-500/10 p-5 text-red-100">
                            <div className="text-sm font-semibold">Couldn&apos;t load the report.</div>
                            <p className="mt-1 text-sm text-red-100/80">Please try again in a moment.</p>
                            <button
                                type="button"
                                onClick={() => {
                                    dailyQuery.refetch();
                                    weeklyQuery.refetch();
                                    monthlyQuery.refetch();
                                    allTimeQuery.refetch();
                                    dailyProjectQuery.refetch();
                                    weeklyProjectQuery.refetch();
                                    monthlyProjectQuery.refetch();
                                    allTimeProjectQuery.refetch();
                                }}
                                className="mt-4 rounded-xl border border-red-300/30 bg-red-500/15 px-4 py-2 text-sm font-medium text-red-50 transition hover:bg-red-500/25"
                            >
                                Retry
                            </button>
                        </div>
                    ) : null}

                    {selectedPeriod === 'WEEKLY' || selectedPeriod === 'MONTHLY' ? (
                        <ProductivityScoreChart
                            period={selectedPeriod}
                            points={scoreSeriesQuery.data ?? []}
                            isLoading={scoreSeriesQuery.isLoading}
                            hasError={Boolean(scoreSeriesQuery.error)}
                        />
                    ) : null}

                    <div className="mt-4">
                        <section className="rounded-[24px] border border-white/10 bg-[#1E2230] p-5 shadow-[0_12px_30px_rgba(0,0,0,0.16)]">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-100">Breakdown</h2>
                                    <p className="mt-1 text-sm text-gray-500">A simple placeholder layout that can later hold charts and time-series.</p>
                                </div>
                                <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-gray-400">
                                    {hasSelectedData ? 'Live data' : 'Waiting for data'}
                                </div>
                            </div>

                            <div className="mt-5 space-y-3">
                                <MetricRow
                                    label="Completed tasks"
                                    rawValue={statsByPeriod[selectedPeriod].completedTasks}
                                    displayValue={statsByPeriod[selectedPeriod].completedTasks}
                                    cap={10}
                                    tone="from-emerald-400 to-emerald-500 text-emerald-300"
                                    detail="Progress toward a 10-task daily rhythm."
                                />
                                <MetricRow
                                    label="Focus time"
                                    rawValue={statsByPeriod[selectedPeriod].focusSeconds}
                                    displayValue={formatFocusTime(statsByPeriod[selectedPeriod].focusSeconds)}
                                    cap={14400}
                                    tone="from-cyan-400 to-sky-500 text-cyan-300"
                                    detail="Visualized against a 4-hour focus block."
                                />
                                <MetricRow
                                    label="Pomodoros"
                                    rawValue={statsByPeriod[selectedPeriod].completedPomodoros}
                                    displayValue={statsByPeriod[selectedPeriod].completedPomodoros}
                                    cap={8}
                                    tone="from-violet-400 to-fuchsia-500 text-violet-300"
                                    detail="A compact productivity streak target."
                                />
                                <MetricRow
                                    label="Productivity score"
                                    rawValue={statsByPeriod[selectedPeriod].productivityScore ?? 0}
                                    displayValue={statsByPeriod[selectedPeriod].productivityScore ?? 0}
                                    cap={100}
                                    tone="from-amber-400 to-orange-500 text-amber-300"
                                    detail="Score earned from completed pomodoros and tracked items."
                                />
                                <MetricRow
                                    label="Reviewed notes"
                                    rawValue={statsByPeriod[selectedPeriod].reviewedNotes ?? 0}
                                    displayValue={statsByPeriod[selectedPeriod].reviewedNotes ?? 0}
                                    cap={10}
                                    tone="from-blue-400 to-indigo-500 text-blue-300"
                                    detail="Notes reviewed during the selected period."
                                />
                                <MetricRow
                                    label="Reviewed cards"
                                    rawValue={statsByPeriod[selectedPeriod].reviewedCards ?? 0}
                                    displayValue={statsByPeriod[selectedPeriod].reviewedCards ?? 0}
                                    cap={20}
                                    tone="from-fuchsia-400 to-pink-500 text-fuchsia-300"
                                    detail="Recall cards reviewed during the selected period."
                                />
                            </div>
                        </section>

                    </div>

                    <ProjectStatsSection
                        title={selectedPeriodConfig.projectTitle}
                        badgeLabel={selectedPeriodConfig.projectBadgeLabel}
                        projects={projectsByPeriod[selectedPeriod]}
                        hasError={projectErrorByPeriod[selectedPeriod]}
                    />

                </div>
            </div>
        </div>
    );
};
